import { useState, useCallback } from 'react';
import { useScanAlertSound } from './useScanAlertSound';

// ============================================================================
// USE SCAN ALERTS - OKUTULAN BARKOD SEPETE EKLENEMEDİĞİNDE BİRİKEN UYARILAR
// ============================================================================

/**
 * Vardiya boyunca sınırsız birikmesin; en yeniler başta tutulur ve
 * bu sayıyı aşanlar listenin sonundan düşer.
 */
const MAX_ALERTS = 20;

export interface ScanAlert {
  id: string;
  /** Okutulan barkod ya da arama metni. */
  term: string;
  /** "Bulunamadı" | "Stok Yok" | "Fiyat Tanımsız" | "Tartı Ürünü Bulunamadı" */
  title: string;
  /** Listede gösterilecek açıklama. */
  message: string;
  /** Satırda "Hızlı Stok Kaydı" butonu çıksın mı. Ürün zaten varsa (stok/fiyat
   *  sorunu) yeni kart açmak yanlış olacağı için false gelir. */
  canQuickAdd: boolean;
  at: number;
}

/** pushScanAlert'e verilen kayıt; id ve zaman damgası hook tarafından üretilir. */
export type ScanAlertInput = Omit<ScanAlert, 'id' | 'at'>;

export interface UseScanAlertsReturn {
  alerts: ScanAlert[];
  pushScanAlert: (alert: ScanAlertInput) => void;
  dismissAlert: (id: string) => void;
  clearAlerts: () => void;

  isListOpen: boolean;
  openList: () => void;
  closeList: () => void;

  /** Hızlı stok kaydı modalına ön dolgu olarak geçilecek barkod. */
  quickAddBarcode: string | null;
  setQuickAddBarcode: (barcode: string | null) => void;
}

/**
 * Kasiyer barkodları ekrana bakmadan okuttuğu için, okutulan bir ürün sepete
 * eklenemediğinde engelleyici bir modal açmak yerine uyarı burada biriktirilir:
 * ses çalar, sepet bölümündeki şerit sayacı artar, kasiyer fırsat bulduğunda
 * listeye bakıp ürünü ekler veya uyarıyı kapatır.
 *
 * Böylece okutma akışı hiç kesilmez; sonraki barkodlar normal şekilde sepete girer.
 */
export const useScanAlerts = (): UseScanAlertsReturn => {
  const { playScanAlertSound } = useScanAlertSound();

  const [alerts, setAlerts] = useState<ScanAlert[]>([]);
  const [isListOpen, setIsListOpen] = useState(false);
  const [quickAddBarcode, setQuickAddBarcode] = useState<string | null>(null);

  const pushScanAlert = useCallback((alert: ScanAlertInput) => {
    const entry: ScanAlert = {
      ...alert,
      id:
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(16).slice(2)}`,
      at: Date.now(),
    };

    setAlerts((prev) => [entry, ...prev].slice(0, MAX_ALERTS));
    playScanAlertSound();
  }, [playScanAlertSound]);

  const dismissAlert = useCallback((id: string) => {
    setAlerts((prev) => prev.filter((a) => a.id !== id));
  }, []);

  const clearAlerts = useCallback(() => {
    setAlerts([]);
  }, []);

  const openList = useCallback(() => setIsListOpen(true), []);
  const closeList = useCallback(() => setIsListOpen(false), []);

  return {
    alerts,
    pushScanAlert,
    dismissAlert,
    clearAlerts,
    isListOpen,
    openList,
    closeList,
    quickAddBarcode,
    setQuickAddBarcode,
  };
};

export default useScanAlerts;
